/**
 * Shared ordered-comparison semantics for client-side `FilterGroup[]`
 * evaluation. ONE implementation, imported by both in-memory evaluators:
 *
 *   - `electron/data-compare/dataComparer.ts` (Data Compare results grid)
 *   - `src/features/workspace/common/utils/filterEvaluator.ts`
 *     (loose-FK badge gating in Table Explorer / Query Editor)
 *
 * Why this file exists (B10 / F02-P4): those two used to disagree three ways
 * on identical input. `dataComparer` did numeric → date → lexicographic;
 * `filterEvaluator` was numeric-only and returned **false** for every
 * non-numeric operand, with no date branch at all. So the same date-range
 * filter behaved differently depending on which surface the user was on.
 *
 * The richer semantics won, for two reasons:
 *   1. "Silently matches nothing" is the exact failure mode the Soporte-3 wave
 *      exists to remove. A date or text range that quietly returns zero rows
 *      is worse than one that answers.
 *   2. `filterEvaluator`'s own contract is to mirror `buildFilterWhereClause`
 *      — and SQL orders dates and text perfectly well. Returning false was a
 *      gap against its stated contract, not a deliberate narrowing. F02-P3
 *      already recorded it as "needs date-range extension first".
 *
 * Deliberately NOT unified here: equality. `dataComparer` compares `=` / `!=`
 * case-INsensitively (it filters display text in a results grid, where "find
 * what I typed" is the expectation, and MSSQL/MySQL default collations agree);
 * `filterEvaluator` compares case-SENSITIVELY (it stands in for a real WHERE
 * clause, and PG/Oracle/SQLite are case-sensitive). Neither default is correct
 * for all five engines, and silently flipping either would change results on a
 * surface no single task owns. Both sites carry a comment pointing here.
 *
 * Kept dependency-free on purpose — `dataComparer.ts` runs in the Electron
 * main process and must not pull renderer code in through this import. The
 * one exception is `decimalKey` from `sqlNumericLiteral.ts`, itself
 * dependency-free (a regex + `./columnAlignment`, a pure module) — see
 * `numericEquals` below.
 */

import { decimalKey } from './sqlNumericLiteral';

/** Ordered (range) comparison operators this module handles. */
export type OrderedFilterOperator = '<' | '<=' | '>' | '>=';

/**
 * Coerce to a finite number, or `null` when the value isn't numeric.
 * Accepts the string-shaped operands adapters produce (the MSSQL STS route
 * maps every cell to its `displayValue` string) so an int column still
 * compares as an int — the F02 bug in one line.
 */
export function asNumber(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) return Number(v);
  return null;
}

/**
 * Coerce to a Date, or `null`. Callers MUST try `asNumber` first: bare numeric
 * strings are valid dates to `Date.parse` (`'101523'` parses as year 101523),
 * which is precisely how the reported bug produced "13, 14, then 101523+".
 *
 * Exported for `sameDateInstant`: the same `Date.parse` this module already
 * uses for `<`/`>`/`BETWEEN` is what Data Compare's `=`/`IN` reuse for a known
 * datetime column, rather than inventing a second date parser that could
 * disagree with the ordering this surface already shows the user.
 */
export function asDate(v: unknown): Date | null {
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  if (typeof v === 'string') {
    const parsed = Date.parse(v);
    return Number.isNaN(parsed) ? null : new Date(parsed);
  }
  return null;
}

/** Case-insensitive string form used by the lexicographic fallback. */
function asComparableString(v: unknown): string {
  return String(v ?? '').toLowerCase();
}

function applyOrder(a: number, b: number, operator: OrderedFilterOperator): boolean {
  switch (operator) {
    case '<': return a < b;
    case '<=': return a <= b;
    case '>': return a > b;
    case '>=': return a >= b;
  }
}

/**
 * `rowValue <operator> conditionValue`, resolving the comparison type in a
 * fixed order: numeric → date → case-insensitive lexicographic.
 *
 * The numeric branch MUST come first (see `asDate`).
 */
export function compareOrdered(
  rowValue: unknown,
  conditionValue: unknown,
  operator: OrderedFilterOperator,
): boolean {
  const numRow = asNumber(rowValue);
  const numCondition = asNumber(conditionValue);
  if (numRow !== null && numCondition !== null) {
    return applyOrder(numRow, numCondition, operator);
  }

  const dateRow = asDate(rowValue);
  const dateCondition = asDate(conditionValue);
  if (dateRow !== null && dateCondition !== null) {
    return applyOrder(dateRow.getTime(), dateCondition.getTime(), operator);
  }

  const strRow = asComparableString(rowValue);
  const strCondition = asComparableString(conditionValue);
  switch (operator) {
    case '<': return strRow < strCondition;
    case '<=': return strRow <= strCondition;
    case '>': return strRow > strCondition;
    case '>=': return strRow >= strCondition;
  }
}

/**
 * Inclusive `lo <= rowValue <= hi`, using the same numeric → date →
 * lexicographic resolution as `compareOrdered`. Both bounds are included —
 * the operator's label says so ("between (inclusive)") precisely because a
 * user could not tell before.
 */
export function compareBetween(rowValue: unknown, lo: unknown, hi: unknown): boolean {
  const numRow = asNumber(rowValue);
  const numLo = asNumber(lo);
  const numHi = asNumber(hi);
  if (numRow !== null && numLo !== null && numHi !== null) {
    return numRow >= numLo && numRow <= numHi;
  }

  const dateRow = asDate(rowValue);
  const dateLo = asDate(lo);
  const dateHi = asDate(hi);
  if (dateRow !== null && dateLo !== null && dateHi !== null) {
    const t = dateRow.getTime();
    return t >= dateLo.getTime() && t <= dateHi.getTime();
  }

  const strRow = asComparableString(rowValue);
  return strRow >= asComparableString(lo) && strRow <= asComparableString(hi);
}

/**
 * Exact decimal equality for a KNOWN numeric column's `=`/`IN` — `null` when
 * either side isn't numeric text/a number at all, so the caller can fall back.
 *
 * Deliberately NOT `asNumber` (`Number()`): a `DECIMAL(38,30)` or a bigint
 * past 2^53 rounds through `Number()`, so `'1.000000000000000000000000000001'`
 * would wrongly equal `'1'`. `decimalKey` (from `sqlNumericLiteral.ts`, the
 * same identity the emitters use to write a literal) compares the exact
 * digits instead — it is also how `1`, `1.0` and `1.00` (what the list
 * widget's number field and a driver's decimal string each spell the same
 * value as) come out equal.
 *
 * Only called when the column's category is known to be `number` (Data
 * Compare's `valueCategory` on the condition) — unlike `asNumber`, this is
 * NOT safe to apply to two arbitrary strings: an opaque id column's
 * `'0101522'` and `'101522'` are the same number but must stay unequal
 * (`evaluateFilterCondition`'s D2 rule), which the category gate preserves by
 * never being consulted for a non-numeric column.
 */
export function numericEquals(a: unknown, b: unknown): boolean | null {
  if (typeof a !== 'number' && typeof a !== 'string') return null;
  if (typeof b !== 'number' && typeof b !== 'string') return null;
  const keyA = decimalKey(String(a).trim());
  const keyB = decimalKey(String(b).trim());
  if (keyA === null || keyB === null) return null;
  return keyA === keyB;
}

/**
 * Same instant for a KNOWN datetime column's `=`/`IN` — `null` when either
 * side doesn't parse as a date at all, so the caller can fall back. Reuses
 * `asDate` (this module's own `Date.parse`), so a naive `datetime-local` pick
 * (`'2024-03-01 00:00'`) and a zoned driver timestamp
 * (`'2024-03-01T00:00:00.000Z'`) resolve through the exact same parser
 * `compareOrdered`/`compareBetween` already use for `<`/`BETWEEN` on this
 * column — never a second, possibly-disagreeing date parser.
 */
export function sameDateInstant(a: unknown, b: unknown): boolean | null {
  const da = asDate(a);
  const db = asDate(b);
  if (da === null || db === null) return null;
  return da.getTime() === db.getTime();
}

/**
 * `CONTAINS`, in both evaluators: the typed value as a case-insensitive
 * substring, `%` and `_` included as plain characters — what the SQL the
 * filter emitters write matches (`containsLikeSql.ts`). A NULL cell never
 * contains anything.
 */
export function matchesContains(rowValue: unknown, value: unknown): boolean {
  if (rowValue === null || rowValue === undefined) return false;
  return asComparableString(rowValue).includes(asComparableString(value));
}

/**
 * The **grid-filter** flavour of `LIKE`: case-insensitive, and a pattern with
 * no `%` / `_` is implicitly wrapped so it reads as "contains" — what a user
 * typing into a filter box means. Backs `LIKE` in `dataComparer.ts` (this is
 * the behaviour that surface already had inline).
 *
 * `filterEvaluator.ts` deliberately does NOT use this: it mirrors a real SQL
 * WHERE clause, where `LIKE 'Adventure'` does not match 'AdventureWorks'. That
 * difference is intentional and load-bearing, so it keeps its own strict
 * matcher rather than importing this one.
 */
export function matchesLikePattern(rowValue: unknown, pattern: unknown): boolean {
  if (rowValue === null || rowValue === undefined) return false;
  let raw = asComparableString(pattern);
  if (!raw.includes('%') && !raw.includes('_')) {
    raw = `%${raw}%`;
  }
  const regexSource = raw
    .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    .replace(/%/g, '.*')
    .replace(/_/g, '.');
  return new RegExp(`^${regexSource}$`, 'i').test(asComparableString(rowValue));
}
